import type React from 'react';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useFormik } from 'formik';
import { useTranslation } from 'react-i18next';
import { Button } from 'primereact/button';
import { Calendar } from 'primereact/calendar';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { InputTextarea } from 'primereact/inputtextarea';
import { apiServices } from 'apps/marsinet/src/app/services/apiServices';
import { MarsinetContext } from 'apps/marsinet/src/app/context/marsinetProvider';
import { useAddIncidenceValidationSchema } from 'apps/marsinet/src/app/validations/addIncidenceValidationSchema';
import { useToast } from 'apps/marsinet/src/app/context/ToastProvider';
import { FormField } from '../../../devices/components/form-field-add-device/form-field';
import { CreateIncidenceDTO, RpsOptionDto, CreateFormOptionsDto } from 'apps/marsinet/src/app/typescript-axios';
import { normalizeApiError } from 'apps/marsinet/src/app/errors/normalize-api-error';
import {
  getLocalDateTimeInTimeZone,
  localDateTimeToUtc,
} from 'apps/marsinet/src/app/utils/timezone-utils';

/* -------------------------------------------------------------------------- */
/* Types                                                                       */
/* -------------------------------------------------------------------------- */

/** One entry of any RPS catalog returned by getAllCreateFormOptions. */;

type DropdownOption = {
  label: string;
  value: string;
};

type DeviceOption = DropdownOption & {
  model: number | null;
  serial?: string;
};

type IncidenceFormValues = {
  title: string;
  description: string;
  containment_actions: string;
  start_time: Date | null;
  d_id: string;
  priority: string;
  type: string;
  detection: string;
  communication_channel: string;
  severity: string;
  detected_by: string;
};

interface CreateIncidenceProps {
  visible: boolean;
  setVisible: (visible: boolean) => void;
  incidenceFunction: (values: CreateIncidenceDTO, deviceId?: string) => Promise<string>;
  create?: boolean;
  incidence?: Partial<IncidenceFormValues> & { device_id?: string };
}

/* -------------------------------------------------------------------------- */
/* Catalog loading                                                             */
/* -------------------------------------------------------------------------- */

const EMPTY_OPTIONS: CreateFormOptionsDto = {
  severity: [],
  type: [],
  priority: [],
  my_detection: [],
  media: [],
};

/**
 * Loads every RPS catalog the form needs in a single request.
 * Move to its own file if another screen needs the same catalogs.
 */
function useIncidenceFormOptions(enabled: boolean) {
  const [options, setOptions] = useState<CreateFormOptionsDto>(EMPTY_OPTIONS);
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);

  const load = useCallback(async () => {
    setIsLoading(true);
    setHasError(false);
    try {
      const { data } =
        await apiServices.incidenceApi.incidenceControllerGetAllCreateFormOptions();

      setOptions({
        severity: data?.severity ?? [],
        type: data?.type ?? [],
        priority: data?.priority ?? [],
        my_detection: data?.my_detection ?? [],
        media: data?.media ?? [],
      });
    } catch {
      setOptions(EMPTY_OPTIONS);
      setHasError(true);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (enabled) {
      load();
    }
  }, [enabled, load]);

  return { options, isLoading, hasError, reload: load };
}

/* -------------------------------------------------------------------------- */
/* Catalog helpers                                                             */
/* -------------------------------------------------------------------------- */

const toDropdownOptions = (options: RpsOptionDto[]): DropdownOption[] =>
  options
    .filter((option) => option?.id && option?.description)
    .map((option) => ({ label: option.description, value: option.id }));

const ATLAS_MODEL = 5;

/** RPS encodes the device family in the option id/description. */
const TYPE_TOKENS = { atlas: ['A30_'], explorer: ['EXP_'] };
const SEVERITY_TOKENS = { atlas: ['ATLAS', 'A30'], explorer: ['EXP'] };

const filterByDeviceModel = (
  options: DropdownOption[],
  deviceModel: number | null | undefined,
  tokens: { atlas: string[]; explorer: string[] },
): DropdownOption[] => {
  const wanted = deviceModel === ATLAS_MODEL ? tokens.atlas : tokens.explorer;

  return options.filter((option) => {
    const haystack = `${option.label} ${option.value}`.toUpperCase();
    return wanted.some((token) => haystack.includes(token));
  });
};

const EMPTY_FORM_VALUES: IncidenceFormValues = {
  title: '',
  description: '',
  containment_actions: '',
  start_time: new Date(),
  d_id: '',
  priority: '',
  type: '',
  detection: '',
  communication_channel: '',
  severity: '',
  detected_by: '',
};

/** Backend field name -> Formik field name, for 400 responses. */
const API_FIELD_TO_FORM_FIELD: Record<string, keyof IncidenceFormValues> = {
  serial_number: 'd_id',
  serialNumber: 'd_id',
  rpsDeviceId: 'd_id',
  description: 'description',
  containment_actions: 'containment_actions',
  start_time: 'start_time',
  detected_by: 'detected_by',
  priority: 'priority',
  type: 'type',
  detection: 'detection',
  communication_channel: 'communication_channel',
  severity: 'severity',
};

/* -------------------------------------------------------------------------- */
/* Component                                                                   */
/* -------------------------------------------------------------------------- */

const ModalCreateIncidence: React.FC<CreateIncidenceProps> = ({
  visible,
  setVisible,
  incidenceFunction,
  create = true,
  incidence,
}) => {
  const { t } = useTranslation();
  const { setError, selectedTimezone } = useContext(MarsinetContext);
  const toast = useToast();
  const validationSchema = useAddIncidenceValidationSchema();

  const [devices, setDevices] = useState<DeviceOption[]>([]);
  const [detectedByUsers, setDetectedByUsers] = useState<DropdownOption[]>([]);

  const {
    options: rpsOptions,
    isLoading: isLoadingRpsOptions,
    hasError: rpsOptionsError,
    reload: reloadRpsOptions,
  } = useIncidenceFormOptions(visible);

  /* ---------------------------------------------------------------------- */
  /* Initial values                                                          */
  /* ---------------------------------------------------------------------- */

  const initialValues = useMemo<IncidenceFormValues>(() => {
    if (create || !incidence) {
      // Inicializa el selector con la hora actual de la zona seleccionada.
      return {
        ...EMPTY_FORM_VALUES,
        start_time: getLocalDateTimeInTimeZone(new Date(), selectedTimezone),
      };
    }

    return {
      title: incidence.title ?? '',
      description: incidence.description ?? '',
      containment_actions: incidence.containment_actions ?? '',
      start_time: incidence.start_time
        ? getLocalDateTimeInTimeZone(incidence.start_time, selectedTimezone)
        : null,
      d_id: incidence.device_id ?? '',
      priority: incidence.priority ?? '',
      type: incidence.type ?? '',
      detection: incidence.detection ?? '',
      communication_channel: incidence.communication_channel ?? '',
      severity: incidence.severity ?? '',
      detected_by: incidence.detected_by ?? '',
    };
  }, [create, incidence, selectedTimezone]);

  /* ---------------------------------------------------------------------- */
  /* Submit                                                                  */
  /* ---------------------------------------------------------------------- */

  const formik = useFormik<IncidenceFormValues>({
    initialValues,
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (values, { setFieldError, resetForm }) => {
      const device = devices.find((item) => item.value === values.d_id);

      try {
        // Convierte la hora introducida a UTC antes de llamar a la API.
        const payload: CreateIncidenceDTO = {
          description: values.description.trim(),
          containment_actions: values.containment_actions.trim() || undefined,
          start_time: values.start_time
            ? new Date(
              localDateTimeToUtc(values.start_time, selectedTimezone)
            )
            : undefined,
          serial_number: device?.serial || undefined,
          detected_by: values.detected_by.trim() || undefined,
          priority: values.priority || undefined,
          type: values.type || undefined,
          detection: values.detection || undefined,
          communication_channel: values.communication_channel || undefined,
          severity: values.severity || undefined,
        };

        await incidenceFunction(payload, values.d_id || undefined);

        resetForm();
        setVisible(false);
      } catch (error: any) {
        if (error instanceof RangeError && values.start_time) {
          setFieldError('start_time', error.message);
          return;
        }
        const responseData = error?.response?.data;

        // Keep the dialog open so the user does not lose what they typed.
        if (error?.response?.status === 400 && responseData?.missingFields) {
          responseData.missingFields.forEach((field: string) => {
            setFieldError(
              API_FIELD_TO_FORM_FIELD[field] ?? field,
              t('validations.required'),
            );
          });
          return;
        }

        const normalizedError = normalizeApiError(error);

        toast.showToast(
          'error',
          t('messages.error'),
          t(normalizedError.messageKey, normalizedError.params),
        );
      }
    },
  });

  /* ---------------------------------------------------------------------- */
  /* Derived dropdown options                                                */
  /* ---------------------------------------------------------------------- */

  const selectedDevice = useMemo(
    () => devices.find((device) => device.value === formik.values.d_id),
    [devices, formik.values.d_id],
  );

  const hasSelectedDevice = Boolean(selectedDevice);
  const selectedDeviceModel = selectedDevice?.model ?? null;

  const priorityOptions = useMemo(
    () => toDropdownOptions(rpsOptions.priority),
    [rpsOptions.priority],
  );

  const detectionOptions = useMemo(
    () => toDropdownOptions(rpsOptions.my_detection),
    [rpsOptions.my_detection],
  );

  const communicationChannelOptions = useMemo(
    () => toDropdownOptions(rpsOptions.media),
    [rpsOptions.media],
  );

  const typeOptions = useMemo(() => {
    if (!hasSelectedDevice) return [];
    return filterByDeviceModel(
      toDropdownOptions(rpsOptions.type),
      selectedDeviceModel,
      TYPE_TOKENS,
    );
  }, [rpsOptions.type, hasSelectedDevice, selectedDeviceModel]);

  const severityOptions = useMemo(() => {
    if (!hasSelectedDevice) return [];
    return filterByDeviceModel(
      toDropdownOptions(rpsOptions.severity),
      selectedDeviceModel,
      SEVERITY_TOKENS,
    );
  }, [rpsOptions.severity, hasSelectedDevice, selectedDeviceModel]);

  /* Clear selections that no longer exist after the device changes. */
  const { type: selectedType, severity: selectedSeverity } = formik.values;
  const { setFieldValue } = formik;

  useEffect(() => {
    if (selectedType && !typeOptions.some((o) => o.value === selectedType)) {
      setFieldValue('type', '');
    }
  }, [typeOptions, selectedType, setFieldValue]);

  useEffect(() => {
    if (
      selectedSeverity &&
      !severityOptions.some((o) => o.value === selectedSeverity)
    ) {
      setFieldValue('severity', '');
    }
  }, [severityOptions, selectedSeverity, setFieldValue]);

  /* ---------------------------------------------------------------------- */
  /* Devices and "detected by"                                               */
  /* ---------------------------------------------------------------------- */

  const loadDevices = useCallback(async () => {
    try {
      const { data } =
        await apiServices.myAccountApi.myAccountControllerGetMyDevices();

      setDevices(
        (data?.devices ?? []).map((device) => ({
          label: `${device.model === ATLAS_MODEL ? 'AT' : 'EXP'}-${device.serial}`,
          value: device.d_id,
          model: device.model ?? null,
          serial: device.serial ?? undefined,
        })),
      );
    } catch {
      setError(true);
    }
  }, [setError]);

  const loadDetectedByUsers = useCallback(async () => {
    try {
      const { data } =
        await apiServices.userApi.usersControllerGetIncidentDetectedByOptions();

      const isMarsiOrganization = data?.is_marsi_organization ?? false;
      const organizationName = data?.organization_name ?? '';
      const defaultDetectedBy = data?.default_detected_by ?? '';
      const currentUserEmail = data?.current_user_email ?? '';

      const options: DropdownOption[] = (data?.options ?? []).map((option) => ({
        label: option.email,
        value: option.email,
      }));

      const defaultValue = isMarsiOrganization
        ? currentUserEmail ||
        (defaultDetectedBy.includes('@') ? defaultDetectedBy : '')
        : defaultDetectedBy;

      if (defaultValue && !options.some((o) => o.value === defaultValue)) {
        options.unshift({
          label: isMarsiOrganization
            ? defaultValue
            : organizationName || defaultValue,
          value: defaultValue,
        });
      }

      setDetectedByUsers(options);

      if (create && defaultValue) {
        setFieldValue('detected_by', defaultValue);
      }
    } catch {
      setError(true);
    }
  }, [create, setError, setFieldValue]);

  useEffect(() => {
    if (visible) {
      loadDevices();
      loadDetectedByUsers();
    }
  }, [visible, loadDevices, loadDetectedByUsers]);

  /* ---------------------------------------------------------------------- */
  /* Dialog helpers                                                          */
  /* ---------------------------------------------------------------------- */

  const onPageHide = () => {
    formik.resetForm();
    setVisible(false);
  };

  const isFieldInvalid = (name: keyof IncidenceFormValues) =>
    Boolean(formik.touched[name] && formik.errors[name]);

  const fieldError = (name: keyof IncidenceFormValues) =>
    isFieldInvalid(name) ? String(formik.errors[name]) : null;

  const requiredMark = (
    <span className="text-red-500 ml-1" aria-hidden="true">
      *
    </span>
  );

  const catalogsUnavailable =
    !isLoadingRpsOptions &&
    !rpsOptionsError &&
    (priorityOptions.length === 0 ||
      (hasSelectedDevice &&
        (typeOptions.length === 0 || severityOptions.length === 0)));

  const catalogsDisabled = isLoadingRpsOptions || rpsOptionsError;

  const canSubmit =
    formik.isValid &&
    formik.dirty &&
    !formik.isSubmitting &&
    !catalogsUnavailable;

  /* ---------------------------------------------------------------------- */
  /* Render                                                                  */
  /* ---------------------------------------------------------------------- */

  return (
    <Dialog
      header={
        <div>
          <h2>
            {create
              ? t('pages.incidents.createIncident')
              : t('pages.incidents.edit')}
          </h2>
          <p className="text-sm text-gray-500">
            {t('pages.incidents.create_description')}
          </p>
        </div>
      }
      visible={visible}
      className="mx-2 md:w-16 lg:w-10 xl:w-10"
      onHide={onPageHide}
      breakpoints={{ '960px': '75vw', '641px': '100vw' }}
    >
      <form autoComplete="off" onSubmit={formik.handleSubmit}>
        <div className="grid formgrid p-fluid">
          {(isLoadingRpsOptions || rpsOptionsError) && (
            <div className="field col-12">
              <div className="p-3 border-round border-1 surface-100 border-300 flex align-items-center justify-content-between gap-2">
                <span className="text-sm">
                  {isLoadingRpsOptions
                    ? 'Cargando opciones de RPS...'
                    : 'No se pudieron cargar las opciones de RPS.'}
                </span>
                {rpsOptionsError && (
                  <Button
                    type="button"
                    label="Reintentar"
                    icon="pi pi-refresh"
                    severity="secondary"
                    outlined
                    onClick={reloadRpsOptions}
                  />
                )}
              </div>
            </div>
          )}

          {!create && (
            <FormField
              id="input-create-incidence-title"
              label="pages.incidents.title"
              name="title"
              value={formik.values.title}
              placeholder="pages.incidents.title"
              onBlur={formik.handleBlur}
              onChange={(e) => formik.setFieldValue('title', e.target.value)}
              isInvalid={isFieldInvalid('title')}
              errorMessage={fieldError('title')}
              className="col-12 md:col-6"
            />
          )}

          {/* Descripción */}
          <div className="field col-12 md:col-6">
            <label
              htmlFor="input-create-incidence-description"
              className="font-bold"
            >
              {t('pages.incidents.description_title')}
              {requiredMark}
            </label>
            <InputTextarea
              id="input-create-incidence-description"
              name="description"
              value={formik.values.description}
              placeholder={t('pages.incidents.description')}
              maxLength={4000}
              rows={1}
              onBlur={formik.handleBlur}
              onChange={formik.handleChange}
              className={isFieldInvalid('description') ? 'p-invalid' : ''}
            />
            {fieldError('description') && (
              <small className="p-error">{fieldError('description')}</small>
            )}
          </div>


          {/* Acciones de contención */}
          <div className="field col-12 md:col-6">
            <label
              htmlFor="input-create-incidence-containment_actions"
              className="font-bold"
            >
              {t('pages.incidents.containment_actions')}
            </label>
            <InputTextarea
              id="input-create-incidence-containment_actions"
              name="containment_actions"
              value={formik.values.containment_actions}
              placeholder={t('pages.incidents.containment_actions')}
              maxLength={4000}
              rows={1}
              onBlur={formik.handleBlur}
              onChange={formik.handleChange}
              className={
                isFieldInvalid('containment_actions') ? 'p-invalid' : ''
              }
            />
            {fieldError('containment_actions') && (
              <small className="p-error">
                {fieldError('containment_actions')}
              </small>
            )}
          </div>

          {/* Fecha de detección */}
          <div className="field col-12 md:col-6">
            <label
              htmlFor="input-create-incidence-start_time"
              className="font-bold"
            >
              {t('pages.incidents.date')}
              {requiredMark}
            </label>
            <Calendar
              id="input-create-incidence-start_time"
              name="start_time"
              value={formik.values.start_time}
              placeholder="dd/MM/yyyy HH:mm"
              dateFormat="dd/mm/yy"
              hourFormat="24"
              showIcon
              showTime
              readOnlyInput
              onBlur={formik.handleBlur}
              onChange={(e) => formik.setFieldValue('start_time', e.value ?? null)}
              className={`w-full ${isFieldInvalid('start_time') ? 'p-invalid' : ''}`}
            />
            {fieldError('start_time') && (
              <small className="p-error">{fieldError('start_time')}</small>
            )}
          </div>

          {/* Dispositivo -> serial_number */}
          <div className="field col-12 md:col-6">
            <label htmlFor="input-create-incidence-d_id" className="font-bold">
              {t('pages.incidents.d_id')}
              {requiredMark}
            </label>
            <Dropdown
              id="input-create-incidence-d_id"
              name="d_id"
              value={formik.values.d_id}
              options={devices}
              optionLabel="label"
              optionValue="value"
              placeholder={t('pages.incidents.d_id')}
              filter
              filterBy="label"
              filterMatchMode="contains"
              emptyMessage="No hay dispositivos disponibles"
              emptyFilterMessage="No se ha encontrado ningún dispositivo"
              onBlur={formik.handleBlur}
              onChange={(event) => {
                formik.setFieldValue('d_id', event.value);
                // type y severity dependen del modelo del dispositivo.
                formik.setFieldValue('type', '', false);
                formik.setFieldValue('severity', '', false);
              }}
              className={`w-full ${isFieldInvalid('d_id') ? 'p-invalid' : ''}`}
            />
            {fieldError('d_id') && (
              <small className="p-error">{fieldError('d_id')}</small>
            )}
          </div>

          {/* Tipo */}
          <div className="field col-12 md:col-6">
            <label htmlFor="input-create-incidence-type" className="font-bold">
              {t('pages.incidents.type')}
              {requiredMark}
            </label>
            <Dropdown
              id="input-create-incidence-type"
              name="type"
              value={formik.values.type}
              options={typeOptions}
              placeholder={
                isLoadingRpsOptions
                  ? 'Cargando tipo...'
                  : !hasSelectedDevice
                    ? 'Selecciona primero un dispositivo'
                    : t('pages.incidents.type')
              }
              filter
              filterBy="label"
              filterMatchMode="contains"
              disabled={catalogsDisabled || !hasSelectedDevice}
              onBlur={formik.handleBlur}
              onChange={(e) => formik.setFieldValue('type', e.value)}
              className={isFieldInvalid('type') ? 'p-invalid' : ''}
            />
            {fieldError('type') && (
              <small className="p-error">{fieldError('type')}</small>
            )}
          </div>

          {/* Impacto / gravedad -> severity */}
          <div className="field col-12 md:col-6">
            <label
              htmlFor="input-create-incidence-severity"
              className="font-bold"
            >
              {t('pages.incidents.impact')}
              {requiredMark}
            </label>
            <Dropdown
              id="input-create-incidence-severity"
              name="severity"
              value={formik.values.severity}
              options={severityOptions}
              placeholder={
                isLoadingRpsOptions
                  ? 'Cargando impacto...'
                  : !hasSelectedDevice
                    ? 'Selecciona primero un dispositivo'
                    : t('pages.incidents.impact')
              }
              filter
              filterBy="label"
              filterMatchMode="contains"
              disabled={catalogsDisabled || !hasSelectedDevice}
              onBlur={formik.handleBlur}
              onChange={(e) => formik.setFieldValue('severity', e.value)}
              className={isFieldInvalid('severity') ? 'p-invalid' : ''}
            />
            {fieldError('severity') && (
              <small className="p-error">{fieldError('severity')}</small>
            )}
          </div>

          {/* Prioridad */}
          <div className="field col-12 md:col-6">
            <label
              htmlFor="input-create-incidence-priority"
              className="font-bold"
            >
              {t('pages.incidents.priority')}
              {requiredMark}
            </label>
            <Dropdown
              id="input-create-incidence-priority"
              name="priority"
              value={formik.values.priority}
              options={priorityOptions}
              placeholder={
                isLoadingRpsOptions
                  ? 'Cargando prioridad...'
                  : t('pages.incidents.priority')
              }
              disabled={catalogsDisabled}
              onBlur={formik.handleBlur}
              onChange={(e) => formik.setFieldValue('priority', e.value)}
              className={isFieldInvalid('priority') ? 'p-invalid' : ''}
            />
            {fieldError('priority') && (
              <small className="p-error">{fieldError('priority')}</small>
            )}
          </div>

          {/* Detectado por */}
          <div className="field col-12 md:col-6">
            <label
              htmlFor="input-create-incidence-detected_by"
              className="font-bold"
            >
              {t('pages.incidents.detected')}
              {requiredMark}
            </label>
            <Dropdown
              id="input-create-incidence-detected_by"
              name="detected_by"
              value={formik.values.detected_by}
              options={detectedByUsers}
              placeholder="Selecciona usuario"
              filter
              filterBy="label"
              filterMatchMode="contains"
              onBlur={formik.handleBlur}
              onChange={(e) => formik.setFieldValue('detected_by', e.value)}
              className={isFieldInvalid('detected_by') ? 'p-invalid' : ''}
            />
            {fieldError('detected_by') && (
              <small className="p-error">{fieldError('detected_by')}</small>
            )}
          </div>

          {/* Detección */}
          <div className="field col-12 md:col-6">
            <label
              htmlFor="input-create-incidence-detection"
              className="font-bold"
            >
              {t('pages.incidents.detection')}
              {requiredMark}
            </label>
            <Dropdown
              id="input-create-incidence-detection"
              name="detection"
              value={formik.values.detection}
              options={detectionOptions}
              placeholder={
                isLoadingRpsOptions
                  ? 'Cargando detección...'
                  : 'Selecciona detección'
              }
              filter
              filterBy="label"
              filterMatchMode="contains"
              disabled={catalogsDisabled}
              onBlur={formik.handleBlur}
              onChange={(e) => formik.setFieldValue('detection', e.value)}
              className={isFieldInvalid('detection') ? 'p-invalid' : ''}
            />
            {fieldError('detection') && (
              <small className="p-error">{fieldError('detection')}</small>
            )}
          </div>

          {/* Canal de comunicación */}
          <div className="field col-12 md:col-6">
            <label
              htmlFor="input-create-incidence-communication_channel"
              className="font-bold"
            >
              {t('pages.incidents.comunication_channel')}
              {requiredMark}
            </label>
            <Dropdown
              id="input-create-incidence-communication_channel"
              name="communication_channel"
              value={formik.values.communication_channel}
              options={communicationChannelOptions}
              placeholder="Selecciona medio"
              disabled={catalogsDisabled}
              onBlur={formik.handleBlur}
              onChange={(e) =>
                formik.setFieldValue('communication_channel', e.value)
              }
              className={
                isFieldInvalid('communication_channel') ? 'p-invalid' : ''
              }
            />
            {fieldError('communication_channel') && (
              <small className="p-error">
                {fieldError('communication_channel')}
              </small>
            )}
          </div>
        </div>

        <div className="border-top-1 surface-border mt-3 mb-3" />

        <div className="flex align-items-center justify-content-end">
          {catalogsUnavailable && (
            <small className="p-error mr-3">
              {t('pages.incidents.noOpcionIncidences')}
            </small>
          )}
          <Button
            id="button-create-incidence"
            severity="secondary"
            icon="pi pi-plus"
            label={t('save')}
            className="p-button-raised"
            type="submit"
            loading={formik.isSubmitting}
            disabled={!canSubmit}
          />
        </div>
      </form>
    </Dialog>
  );
};

export default ModalCreateIncidence;